import { useState, useEffect } from 'react'
import { ActionIcon, Badge, Box, Button, Modal, Paper, Text, ThemeIcon, Tooltip } from '@mantine/core'
import {  ChevronRight, ArrowLeft, ArrowRight, ArrowUp, ChevronLeft, CircleHelp, Network, Undo2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { vocabularyApi, type WordFlashcard } from '../../api/vocabulary'
import { FlashCard } from '../../components/vocabulary/FlashCard'
import { useAuth } from '../../context/AuthContext'

type Status = 'known' | 'unknown' | 'familiar' | 'skipped'
const SWIPE_TUTORIAL_STORAGE_KEY = 'sat_vocab_learning_swipe_tutorial_seen'
const antonymBackground = 'var(--mantine-color-orange-light)'
const synonymColor = 'var(--mantine-color-green-7)'
  
const antonymColor = 'var(--mantine-color-orange-6)'
const synonymBackground = 'var(--mantine-color-green-light)'
export function VocabLearning() {
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const [words, setWords] = useState<WordFlashcard[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [results, setResults] = useState<Array<Status | undefined>>([])
  const [showTutorial, setShowTutorial] = useState(false)

  useEffect(() => {
    vocabularyApi.getDailyLearning()
      .then((res) => { setWords(res.data); setLoading(false) })
      .catch(() => { setError('Failed to load vocabulary'); setLoading(false) })
  }, [])

  useEffect(() => {
    if (authLoading) return

    const storageKey = user
      ? `${SWIPE_TUTORIAL_STORAGE_KEY}_${user.id}`
      : SWIPE_TUTORIAL_STORAGE_KEY

    if (localStorage.getItem(storageKey) !== '1') {
      setShowTutorial(true)
    }
  }, [authLoading, user])

  const handleResponse = (status: Status) => {
    const word = words[currentIndex]
    if (status !== 'skipped') {
      vocabularyApi.updateWordStatus(word.id, status, 'learning').catch(() => {})
    }
    setResults((prev) => {
      const next = [...prev]
      next[currentIndex] = status
      return next
    })
    setCurrentIndex((prev) => prev + 1)
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }

  const handlePracticeAgain = () => {
    const notMasteredWords = words.filter((_, index) => {
      const result = results[index]
      return result === 'unknown' || result === 'familiar'
    })

    if (notMasteredWords.length === 0) {
      return
    }

    setWords(notMasteredWords)
    setResults([])
    setCurrentIndex(0)
  }

  const closeTutorial = () => {
    const storageKey = user
      ? `${SWIPE_TUTORIAL_STORAGE_KEY}_${user.id}`
      : SWIPE_TUTORIAL_STORAGE_KEY

    localStorage.setItem(storageKey, '1')
    setShowTutorial(false)
  }

  const total = words.length
  const done = currentIndex >= total && total > 0
  const progress = total > 0 ? (currentIndex / total) * 100 : 0

  const knownCount = results.filter((r) => r === 'known').length
  const unknownCount = results.filter((r) => r === 'unknown').length
  const familiarCount = results.filter((r) => r === 'familiar').length
  const notMasteredCount = unknownCount + familiarCount
  const stillLearningCount = total - knownCount
  const knownPercent = total > 0 ? Math.round((knownCount / total) * 100) : 0
  const xp = knownCount * 10 + familiarCount * 5
  const pageStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    minHeight: '80vh',
    background: 'var(--sat-bg)',
    color: 'var(--sat-text)',
    padding: '24px 16px',
  }
  const ctaButtonStyle = {
    background: 'var(--sat-green)',
    color: 'var(--mantine-color-white)',
    border: '1px solid var(--sat-border)',
    borderRadius: 10,
    padding: '12px 32px',
    fontSize: 20.8,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
    boxShadow: '0 4px 20px var(--sat-shadow)',
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', background: 'var(--sat-bg)' }}>
        <Text style={{ fontSize: 23.4, color: 'var(--sat-green)' }}>Loading words...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', background: 'var(--sat-bg)' }}>
        <Text style={{ fontSize: 23.4, color: 'var(--sat-red)' }}>{error}</Text>
      </Box>
    )
  }

  if (total === 0) {
    return (
      <Box style={{ ...pageStyle, justifyContent: 'center', gap: 16 }}>
        <Text style={{ fontSize: 31.2, fontWeight: 600, color: 'var(--sat-green)' }}>No new words for today!</Text>
        <Text style={{ fontSize: 19.5, color: 'var(--sat-text-dim)' }}>You have completed today's learning session.</Text>
        <button
          onClick={() => navigate('/dashboard')}
          style={ctaButtonStyle}
        >
          Back to Dashboard
        </button>
      </Box>
    )
  }

  if (done) {
    return (
      <Box
        style={{
          ...pageStyle,
          justifyContent: 'space-between',
          padding: '28px 20px 10px',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        <Box  pt="xl" style={{ width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 28 }}>
          <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'  }}>
            <Text
              style={{
                fontSize: 28,
                fontWeight: 600,
                color: 'var(--sat-text)',
                lineHeight: 1.15,
                maxWidth: 320,
              }}
            >
              Great Progress! Now let&apos;s try some practice questions in Learn.
            </Text>
            <CompletionIllustration />
          </Box>

          <Box  mt="xl" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Text style={{ fontSize: 23, fontWeight: 500, color: 'var(--sat-text-dim)' }}>
              Your progress
            </Text>

            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <Box
              mt="md"
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'var(--mantine-color-orange-5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 24px rgba(253, 149, 62, 0.18)',
                }}
              >
                <Box
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'var(--sat-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'inset 0 0 0 1px var(--sat-border-soft)',
                  }}
                >
                  <Text style={{ fontSize: 36.4, fontWeight: 800, color: 'var(--sat-text-dim)' }}>
                    {knownPercent}%
                  </Text>
                </Box>
              </Box>

              <Box style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <ResultSummaryPill
                  label="Know"
                  value={knownCount}
                  background="var(--sat-green-soft)"
                  textColor="var(--sat-text)"
                />
                <ResultSummaryPill
                  label="Still learning"
                  value={stillLearningCount}
                  background="var(--sat-red-soft)"
                  textColor="var(--sat-text)"
                />
              </Box>
            </Box>

         {/*    <Button
              variant="subtle"
              color="indigo"
              size="md"
              onClick={() => setCurrentIndex(Math.max(total - 1, 0))}
              style={{ alignSelf: 'center', fontWeight: 700 }}
            >
              Back to last question
            </Button> */}
          </Box>
        </Box>

        <Box style={{ width: '100%', maxWidth: 520, display: 'flex', gap: 14   }}>
          <Button
            radius="xl"
            size="xl"
            onClick={handlePracticeAgain}
            disabled={notMasteredCount === 0}
            style={{
              flex: 1,
              height: 60,
              fontSize: 18,
              fontWeight: 400,
              background: 'var(--mantine-color-orange-5)',
              color: 'var(--mantine-color-white)',
              boxShadow: '0 10px 24px rgba(92, 107, 192, 0.18)',
            }}
          >
            {notMasteredCount === 0 ? 'Horrrrrray!' : `Practice Word (${notMasteredCount})`}
          </Button>

          <Button
            radius="xl"
            size="xl"
            variant="filled"
            onClick={() => navigate('/vocabulary/review')}
            style={{
              flex: 1,
              height: 60,
              fontSize: 18,
              fontWeight: 400,
              background: 'var(--sat-surface-info)',
              color: 'var(--sat-info-text)',
            }}
          >
            Review Words 
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box style={pageStyle}>
      <Modal
        opened={showTutorial}
        onClose={closeTutorial}
        centered
        title={<Text style={{ fontSize: 26, fontWeight: 700 }}>How swiping works</Text>}
        radius="lg"
        overlayProps={{ backgroundOpacity: 0.7, blur: 4 }}
      >
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Text style={{ fontSize: 18.2, color: 'var(--sat-text-dim)', lineHeight: 1.6 }}>
            Swipe the card in the direction that best matches how well you know the word.
          </Text>

          <SwipeHintCard
            icon={<ArrowLeft size={18} />}
            color="var(--sat-red)"
            background="var(--sat-red-soft)"
            title="Swipe left"
            description="For words you have never seen before."
          />
          <SwipeHintCard
            icon={<ArrowRight size={18} />}
            color="var(--sat-green)"
            background="var(--sat-green-soft)"
            title="Swipe right"
            description="For words you already know."
          />
          <SwipeHintCard
            icon={<ArrowUp size={18} />}
            color="var(--sat-warning-text)"
            background="var(--sat-yellow-soft)"
            title="Swipe up"
            description="For words you have seen but do not know exactly."
          />
          <SwipeHintCard
            icon={<Network size={18} />}
            color="var(--sat-warning-text)"
            background="var(--sat-yellow-soft)"
            title="Related words"
            
          >
            <span  style={{ background: antonymBackground, color: antonymColor, border: `1px solid ${antonymColor}`, fontSize: 14, padding: '4px 11px', borderRadius: 12, fontWeight: 600 }}>
              Antonyms</span> 
           <span   style={{ background: synonymBackground, color: synonymColor, border: `1px solid ${synonymColor}`, fontSize: 14, padding: '4px 11px', borderRadius: 12, fontWeight: 600 }}>
            Synonyms</span> 
          <span  style={{ background: 'var(--mantine-color-blue-light)', color: 'var(--mantine-color-blue-7)', border: '1px solid var(--mantine-color-blue-6)', fontSize: 14, padding: '4px 11px', borderRadius: 12, fontWeight: 600 }}>Words family</span> 
         
            </SwipeHintCard>
          
       {/*    <Button color="eveningSea" radius="xl" size="md" onClick={closeTutorial}>
            Start learning
          </Button> */}
        </Box>
      </Modal>

      {/* Progress bar */}
      <Box style={{ width: '100%', maxWidth: 400, marginBottom: 24 }}>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 16.9, color: 'var(--sat-text)', marginBottom: 8, gap: 12 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip label="Unknown words" withArrow>
              <Badge
                size="lg"
                radius="sm"
                style={{ background: 'var(--sat-red-soft)', color: 'var(--sat-red)' }}
              >
                {unknownCount}
              </Badge>
            </Tooltip>
            <Tooltip label="Familiar words" withArrow>
              <Badge
                size="lg"
                radius="sm"
                style={{ background: 'var(--sat-yellow-soft)', color: 'var(--sat-warning-text)' }}
              >
                {familiarCount}
              </Badge>
            </Tooltip>
            <Tooltip label="Known words" withArrow>
              <Badge
                size="lg"
                radius="sm"
                style={{ background: 'var(--sat-green-soft)', color: 'var(--sat-green)' }}
              >
                {knownCount}
              </Badge>
            </Tooltip>
          </Box>
          <span>{currentIndex + 1} / {total}</span>
        </Box>
        <Box style={{ width: '100%', height: 8, background: 'var(--sat-blush)', borderRadius: 999 }}>
          <Box style={{ width: `${progress}%`, height: '100%', background: 'var(--sat-red)', borderRadius: 999, transition: 'width 0.3s ease' }} />
        </Box>
      </Box>

      {/* Card */}
      <Box style={{ position: 'relative', width: 350, height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FlashCard
          key={words[currentIndex].id}
          word={words[currentIndex]}
          onSwipeRight={() => handleResponse('known')}
          onSwipeLeft={() => handleResponse('unknown')}
          onSwipeUp={() => handleResponse('familiar')}
          onSwipeDown={() => handleResponse('skipped')}
        />
      </Box>

      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 350, marginTop: 20 }}>
        <ActionIcon
          size={48}
          radius="xl"
          variant="light"
          color="gray"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          aria-label="Previous word"
        >
          <ChevronLeft size={22} />
        </ActionIcon>
        <ActionIcon
          size={48}
          radius="xl"
          variant="light"
          color="yellow"
          onClick={() => setShowTutorial(true)}
          aria-label="Show swipe help"
        >
          <CircleHelp size={22} />
        </ActionIcon>
        <ActionIcon
          size={48}
          radius="xl"
          variant="light"
          color="eveningSea"
          onClick={() => handleResponse('skipped')}
          aria-label="Next word"
        >
          <ChevronRight size={22} />
        </ActionIcon>
      </Box>
    </Box>
  )
}

function ResultSummaryPill({
  label,
  value,
  background,
  textColor,
}: {
  label: string
  value: number
  background: string
  textColor: string
}) {
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '12px 16px',
        background,
        borderRadius: 999,
      }}
    >
      <Text style={{ fontSize: 20.8, fontWeight: 700, color: textColor }}>
        {label}
      </Text>
      <Text style={{ fontSize: 20.8, fontWeight: 800, color: textColor }}>
        {value}
      </Text>
    </Box>
  )
}

function CompletionIllustration() {
  return (
    <Box style={{ position: 'relative', width: 116, height: 96, flexShrink: 0 }}>
      <Box
        style={{
          position: 'absolute',
          top: 0,
          right: 4,
          width: 58,
          height: 58,
          borderRadius: 16,
          background: 'var(--mantine-color-orange-4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 28px rgba(253, 149, 62, 0.25)',
        }}
      >
        <Undo2 size={28} color="white" />
      </Box>

      <Box style={{ position: 'absolute', top: 6, left: 14, width: 34, height: 8, borderRadius: 999, background: 'var(--sat-yellow)', transform: 'rotate(-20deg)' }} />
      <Box style={{ position: 'absolute', top: 0, left: 28, width: 22, height: 6, borderRadius: 999, background: 'var(--mantine-color-violet-4)', transform: 'rotate(8deg)' }} />
      <Box style={{ position: 'absolute', top: 16, left: 66, width: 7, height: 14, borderRadius: 999, background: 'var(--sat-green)', transform: 'rotate(-30deg)' }} />
      <Box style={{ position: 'absolute', bottom: 14, left: 24, width: 9, height: 18, borderRadius: 999, background: 'var(--sat-red)', transform: 'rotate(28deg)' }} />
      <Box style={{ position: 'absolute', bottom: 6, left: 62, width: 24, height: 8, borderRadius: 999, background: 'var(--sat-green)', transform: 'rotate(36deg)' }} />
      <Box style={{ position: 'absolute', bottom: 24, right: 2, width: 8, height: 8, borderRadius: '50%', background: 'var(--sat-yellow)' }} />
    </Box>
  )
}

function SwipeHintCard({
  icon,
  color,
  background,
  title,
  description,
  children,
}: {
  icon: React.ReactNode
  color: string
  background: string
  title: string
  description?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <Paper
      withBorder
      style={{
        background: 'var(--sat-surface)',
        borderColor: 'var(--sat-border)',
        borderRadius: 14,
        padding: '14px 16px',
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <ThemeIcon radius="xl" size={36} style={{ background, color, flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Box>
          <Text style={{ fontSize: 19.5, fontWeight: 700, color: 'var(--sat-text)' }}>{title}</Text>
          {description && (
            <Text style={{ fontSize: 16.9, color: 'var(--sat-text-dim)', lineHeight: 1.5 }}>
              {description}
            </Text>
          )}
          {children}
        </Box>
      </Box>
    </Paper>
  )
}
